import React, { useState, useEffect, useCallback } from "react";
import { Button, Form, Tab, Tabs } from "react-bootstrap";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import { getCSRFToken } from "../utils/csrf.ts";
import {
  PlusCircle,
  CheckCircle,
  PauseCircle,
  AlertCircle,
  Trash2,
  Timer,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AddBrokerModal from "../components/Modals/AddBrokerModal";
import AddAccountModal from "../components/Modals/AddAccountModal";
import StartInstanceModal from "../components/Modals/StartInstanceModal";
import {
  TradingInstance,
  Broker,
  BrokerAccount,
  Algo,
  AssetClass,
  Symbol,
  LinkedAlgo,
} from "../types";

const getAccountLabel = (broker: any, account: any) => {
  const accountId = String(account.account_id || "");
  const normalizedId = accountId.replace("D#", "");
  
  const accountNames: Record<string, string> = {
    "2109573": "marykate",
    "2304256": "2-9/21",
    "2304257": "3-gold-1k-500",
    "2304258": "4-gold-1k-500",
    "2304259": "5-gold-1k-500",
    "2304262": "6-nq-1k-500",
    "2304263": "7-nq-1k-500",
    "2304264": "8-nq-1k-500",
    "2304265": "9-nq-10k-500",
    "2304266": "10-nq-10k-500"
  };

  const nick = accountNames[normalizedId];
  if (nick) {
    return `${nick} (${account.account_id})`;
  }

  const email = broker.login || "";
  let emailNick = email.split("@")[0] || broker.name || "Account";
  if (emailNick.includes("marykate")) {
    emailNick = "marykate";
  }
  return `${emailNick} - #${account.account_number || account.id} (${account.account_id})`;
};

const brokersList = [
  {
    name: "Interactive Brokers",
    assets: "Stocks, Options, Futures, Forex, Crypto, Metals",
  },
  { name: "Oanda", assets: "Forex, Indices" },
  {
    name: "Tradelocker",
    assets: "Forex, Stocks, Indices, Metals, Energies, Crypto",
  },
  { name: "Robinhood", assets: "Stocks, Options, Futures, Crypto" },
  { name: "forex.com", assets: "Forex, Stocks, Futures, Futures Options" },
  { name: "Charles Schwab", assets: "Stocks, Options, Futures, Forex, Metals" },
  { name: "binance.us", assets: "Crypto" },
  { name: "Kraken", assets: "Crypto, Crypto Futures" },
  { name: "Alpaca", assets: "Stocks, Options, Futures, Forex" },
];

interface SelectedAlgo {
  id: number;
  parameters?: any;
}

const LiveTrading: React.FC = () => {
  const navigate = useNavigate();
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [showAddBrokerModal, setShowAddBrokerModal] = useState(false);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<string>("");
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [serverName, setServerName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [accountType, setAccountType] = useState("");
  const [instances, setInstances] = useState<TradingInstance[]>([]);
  const [allAlgos, setAllAlgos] = useState<Algo[]>([]);
  const [allAssetClasses, setAllAssetClasses] = useState<AssetClass[]>([]);
  const [allSymbols, setAllSymbols] = useState<Symbol[]>([]);
  const [algos, setAlgos] = useState<Algo[]>([]);
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [symbols, setSymbols] = useState<Symbol[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showStartInstanceModal, setShowStartInstanceModal] = useState(false);
  const [newInstanceName, setNewInstanceName] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("");
  const [selectedAlgos, setSelectedAlgos] = useState<SelectedAlgo[]>([]);
  const [linkedAlgos, setLinkedAlgos] = useState<LinkedAlgo[]>([]);
  const [filteredInstances, setFilteredInstances] = useState<TradingInstance[]>(
    []
  );
  const [filteredBrokers, setFilteredBrokers] = useState<Broker[]>([]);
  const [activeInstanceTabKey, setActiveInstanceTabKey] = useState("live");
  const [isAddingBroker, setIsAddingBroker] = useState(false);
  const [isAddingAccount, setIsAddingAccount] = useState(false);
  const [isStartingInstance, setIsStartingInstance] = useState(false);

  // Telegram notification state
  const [telegramEnabled, setTelegramEnabled] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  const [telegramChatId, setTelegramChatId] = useState("");
  const [telegramNotifications, setTelegramNotifications] = useState({
    send_entries: true,
    send_updates: true,
    send_exits: true,
    send_system_notifications: true,
    send_hourly_status: true,
    include_emoji: true,
    include_trade_details: true,
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);

      // Fetch instances with linked_algos
      const instancesResponse = await axios.get(
        `${BACKEND_URL}/api/trading-instances`
      );
      console.log("Raw instances data:", instancesResponse.data);

      // Ensure we have an array and process it
      const rawInstances = Array.isArray(instancesResponse.data)
        ? instancesResponse.data
        : [];

      // Ensure linked_algos is properly set
      const processedInstances = rawInstances.map(
        (instance: TradingInstance) => ({
          ...instance,
          linked_algos: instance.linked_algos || [],
        })
      );

      setInstances(processedInstances);
      setFilteredInstances(processedInstances);
      setFilteredBrokers(brokers);

      // Then fetch other data in parallel only if not already loaded
      if (
        forceRefresh ||
        brokers.length === 0 ||
        algos.length === 0 ||
        assetClasses.length === 0
      ) {
        const [brokersRes, algosRes, assetClassesRes, symbolsRes] =
          await Promise.all([
            axios.get(`${BACKEND_URL}/api/brokers`),
            axios.get(`${BACKEND_URL}/api/algos`),
            axios.get(`${BACKEND_URL}/api/asset-classes`),
            axios.get(`${BACKEND_URL}/api/symbols`),
          ]);
        console.log("Symbols: ", symbolsRes.data);
        console.log("Brokers data:", brokersRes.data);
        setBrokers(brokersRes.data);
        setAlgos(algosRes.data);
        setAllAlgos(algosRes.data);
        setAssetClasses(assetClassesRes.data);
        setAllAssetClasses(assetClassesRes.data);
        setSymbols(symbolsRes.data);
        setAllSymbols(symbolsRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      // Set empty arrays on error
      setInstances([]);
      setFilteredInstances([]);
    } finally {
      setLoading(false);
    }
  }, [brokers.length, algos.length, assetClasses.length]);

  // Initial load
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh instances periodically
  useEffect(() => {
    const interval = setInterval(() => {
      axios
        .get(`${BACKEND_URL}/api/trading-instances`)
        .then((response) => {
          const rawInstances = Array.isArray(response.data)
            ? response.data
            : [];
          const processedInstances = rawInstances.map(
            (instance: TradingInstance) => ({
              ...instance,
              linked_algos: instance.linked_algos || [],
            })
          );
          setInstances(processedInstances);
          setFilteredInstances(processedInstances);
        })
        .catch((error) => console.error("Error refreshing instances:", error));
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Update filtered instances when search query changes
  useEffect(() => {
    const query = searchQuery || "";
    if (!query.trim()) {
      setFilteredInstances(instances);
    } else {
      setFilteredInstances(
        instances.filter((instance) =>
          instance.name && typeof instance.name === "string" && instance.name.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
  }, [searchQuery, instances]);

  // Update filtered brokers when search query changes
  useEffect(() => {
    const query = searchQuery || "";
    if (!query.trim()) {
      setFilteredBrokers(brokers);
    } else {
      setFilteredBrokers(
        brokers.filter((broker) =>
          broker.name && typeof broker.name === "string" && broker.name.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
  }, [searchQuery, brokers]);

  const handleOpenAddAccountModal = async (brokerId: number) => {
    try {
      console.log("Fetching broker details for ID:", brokerId);
      const response = await axios.get(`${BACKEND_URL}/api/brokers`);
      const broker = response.data.find((b: Broker) => b.id === brokerId);

      if (broker) {
        console.log("Found broker:", broker);
        console.log("Broker name:", broker.name);
        setSelectedBroker(broker.name);
        setShowAddAccountModal(true);
      } else {
        console.error("Broker not found with ID:", brokerId);
      }
    } catch (error) {
      console.error("Error fetching broker details:", error);
    }
  };

  const handleAddBroker = async (assetMatches: any) => {
    if (!selectedBroker) {
      console.error("All fields are required.");
      return;
    }

    try {
      setIsAddingBroker(true);
      const csrfToken = await getCSRFToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/brokers/`,
        {
          name: selectedBroker,
          username: assetMatches.username,
          password: assetMatches.password,
          asset_matches: {
            assetClasses: assetMatches.assetClasses,
            symbols: assetMatches.symbols,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      // Immediately update brokers state with new broker
      const newBroker = response.data;
      setBrokers((prevBrokers) => [...prevBrokers, newBroker]);

      // Reset form
      setShowAddBrokerModal(false);
      setSelectedBroker("");
      setApiKey("");
      setApiSecret("");

      // Fetch fresh data to ensure everything is in sync
      await fetchData(true);
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string } };
        message: string;
      };
      console.error(
        "Error adding broker:",
        err.response?.data?.error || err.message
      );
    } finally {
      setIsAddingBroker(false);
    }
  };

  const handleAddAccount = async (brokerName: string) => {
    const broker = brokerName.toLowerCase();
    const isIBKR = broker.includes("interactive") || broker.includes("ibkr");
    const finalAccountType = accountType || (isIBKR ? "paper" : "");

    console.log("Adding account with brokerName:", brokerName);
    console.log("Account details:", { accountId, apiKey, apiSecret, accountType: finalAccountType });

    try {
      setIsAddingAccount(true);
      const csrfToken = await getCSRFToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/broker-accounts/`,
        {
          account_id: accountId || null,
          broker: brokerName,
          api_key: apiKey || null,
          api_secret: apiSecret || null,
          server_name: serverName || null,
          account_number: accountNumber || null,
          account_type: finalAccountType,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      // Immediately update brokers state with new account
      const newAccount = response.data;
      setBrokers((prevBrokers) =>
        prevBrokers.map((broker) =>
          broker.name === brokerName
            ? { ...broker, accounts: [...(broker.accounts || []), newAccount] }
            : broker
        )
      );

      // Reset form
      setShowAddAccountModal(false);
      setAccountId("");
      setApiKey("");
      setApiSecret("");
      setServerName("");
      setAccountNumber("");
      setAccountType("");

      // Fetch fresh data to ensure everything is in sync
      await fetchData(true);
    } catch (error) {
      const err = error as {
        response?: { data?: { error?: string } };
        message: string;
      };
      console.error(
        "Error adding account:",
        err.response?.data?.error || err.message
      );
    } finally {
      setIsAddingAccount(false);
    }
  };

  const handleAddAlgo = (algo: Algo) => {
    setSelectedAlgos((prev) => [
      ...prev,
      {
        id: algo.id,
        parameters: algo.parameters,
      },
    ]);
  };

  const handleRemoveAlgo = (algoId: number) => {
    setSelectedAlgos((prev) => prev.filter((a) => a.id !== algoId));
  };

  const handleStartInstance = async () => {
    try {
      setIsStartingInstance(true);
      const csrfToken = await getCSRFToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/trading-instances/`,
        {
          name: newInstanceName,
          broker_account: selectedAccount,
          balance: 0.0,
          profit: 0.0,
          status: "live",
          algos: selectedAlgos.map((a) => a.id),
          telegram_enabled: telegramEnabled,
          telegram_bot_token: telegramBotToken,
          telegram_chat_id: telegramChatId,
          telegram_notifications: telegramNotifications,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
        }
      );

      // Immediately update instances state with new instance
      const newInstance = response.data;

      // Ensure the new instance has required fields before adding
      if (newInstance && newInstance.id) {
        // Ensure name exists, use provided name or default
        if (!newInstance.name) {
          newInstance.name = newInstanceName || `Instance ${newInstance.id}`;
        }

        setInstances((prevInstances) => [...prevInstances, newInstance]);

        // Update filtered instances based on current search query
        const query = searchQuery || "";
        setFilteredInstances((prevFiltered) => {
          if (!query.trim()) {
            return [...prevFiltered, newInstance];
          }
          const shouldInclude = newInstance.name &&
            typeof newInstance.name === "string" &&
            newInstance.name.toLowerCase().includes(query.toLowerCase());
          return shouldInclude ? [...prevFiltered, newInstance] : prevFiltered;
        });
      }

      // Reset form
      setShowStartInstanceModal(false);
      setNewInstanceName("");
      setSelectedAccount("");
      setSelectedAlgos([]);

      // Reset Telegram fields
      setTelegramEnabled(false);
      setTelegramBotToken("");
      setTelegramChatId("");
      setTelegramNotifications({
        send_entries: true,
        send_updates: true,
        send_exits: true,
        send_system_notifications: true,
        send_hourly_status: true,
        include_emoji: true,
        include_trade_details: true,
      });

      // Fetch fresh data to ensure everything is in sync
      await fetchData();
    } catch (error) {
      console.error("Error starting instance:", error);
    } finally {
      setIsStartingInstance(false);
    }
  };

  const handleTabChange = (key: string | null) => {
    if (key) {
      setActiveInstanceTabKey(key);
    }
  };

  const handleInstanceClick = (instanceId: number) => {
    navigate(`/manage-instance/${instanceId}`);
  };

  const handleDeleteBroker = async (brokerId: number) => {
    if (window.confirm("Are you sure you want to delete this broker? This will stop and remove all associated accounts and trading instances.")) {
      try {
        await axios.delete(`http://localhost:8001/api/brokers/${brokerId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting broker:", error);
        alert("Failed to delete broker.");
      }
    }
  };

  const handleDeleteAccount = async (accountId: number) => {
    if (window.confirm("Are you sure you want to delete this account? This will stop and remove all associated trading instances.")) {
      try {
        await axios.delete(`http://localhost:8001/api/broker-accounts/${accountId}`);
        fetchData();
      } catch (error) {
        console.error("Error deleting account:", error);
        alert("Failed to delete account.");
      }
    }
  };

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Typography variant="h4">Live Trading</Typography>
        <div className="d-flex align-items-center">
          <Form.Control
            type="text"
            placeholder="Search"
            className="me-2"
            value={searchQuery || ""}
            onChange={(e) => setSearchQuery(e.target.value || "")}
          />
          <IconButton>
            <SearchIcon />
          </IconButton>
        </div>
      </div>

      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Typography variant="h5">Brokers</Typography>
          <Button
            variant="primary"
            onClick={() => setShowAddBrokerModal(true)}
            disabled={isAddingBroker}
          >
            {isAddingBroker ? "Adding..." : "Add Broker"}
          </Button>
        </div>

        <Tabs defaultActiveKey="active" className="mb-3">
          <Tab eventKey="active" title="Active Brokers">
            <div className="d-flex flex-wrap gap-3">
              {(() => {
                console.log("filteredBrokers:", filteredBrokers);
                console.log("filteredBrokers length:", filteredBrokers?.length);
                if (filteredBrokers && filteredBrokers.length > 0) {
                  console.log("First broker object:", filteredBrokers[0]);
                  console.log("First broker keys:", Object.keys(filteredBrokers[0]));
                }
                return null;
              })()}
              {filteredBrokers &&
                filteredBrokers.map((broker) => (
                  <Card key={broker.id} className="p-2 shadow-sm">
                    <CardContent>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <Typography variant="h6">{broker.name}</Typography>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteBroker(broker.id)}
                          title="Delete Broker"
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </div>
                      <div className="mb-3">
                        {broker.accounts &&
                          broker.accounts.map((account) => (
                            <div key={account.id} className="d-flex justify-content-between align-items-center mb-1">
                              <Typography
                                variant="body2"
                                color="textSecondary"
                              >
                                {getAccountLabel(broker, account)}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAccount(account.id)}
                                title="Delete Account"
                              >
                                <Trash2 size={14} />
                              </IconButton>
                            </div>
                          ))}
                      </div>
                      <Button
                        variant="outline-primary"
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: "30px",
                          height: "30px",
                          minWidth: "30px",
                          padding: "0",
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenAddAccountModal(broker.id);
                        }}
                      >
                        <PlusCircle size={20} />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </Tab>
        </Tabs>
      </div>

      <div>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <Typography variant="h5">Live Instances</Typography>
          <Button
            variant="primary"
            onClick={() => setShowStartInstanceModal(true)}
            disabled={isStartingInstance}
          >
            {isStartingInstance ? "Starting..." : "Start Instance"}
          </Button>
        </div>
        <Tabs
          defaultActiveKey="live"
          activeKey={activeInstanceTabKey}
          onSelect={handleTabChange}
          className="mb-3"
        >
          <Tab
            eventKey="live"
            title={`Live (${(Array.isArray(instances)
              ? instances.filter((i) => i.status === "live")
              : []
            ).length
              })`}
          />
          <Tab
            eventKey="paused"
            title={`Paused (${(Array.isArray(instances)
              ? instances.filter((i) => i.status === "paused")
              : []
            ).length
              })`}
          />
          <Tab
            eventKey="interrupted"
            title={`Interrupted (${(Array.isArray(instances)
              ? instances.filter((i) => i.status === "interrupted")
              : []
            ).length
              })`}
          />
          <Tab
            eventKey="pending"
            title={`Pending (${(Array.isArray(instances)
              ? instances.filter((i) => i.status === "pending")
              : []
            ).length
              })`}
          />
        </Tabs>
        <div className="d-flex flex-wrap gap-3">
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            (Array.isArray(filteredInstances)
              ? filteredInstances.filter(
                (instance) => instance.status === activeInstanceTabKey
              )
              : []
            ).map((instance) => (
              <Card key={instance.id} className="p-2 shadow-sm">
                <CardContent>
                  <div className="d-flex justify-content-between">
                    <Typography variant="h6">{instance.name}</Typography>
                    <IconButton>
                      {instance.status === "live" && (
                        <CheckCircle color="#2196f3" />
                      )}
                      {instance.status === "paused" && (
                        <PauseCircle color="#ff9800" />
                      )}
                      {instance.status === "interrupted" && (
                        <AlertCircle color="#f44336" />
                      )}
                      {instance.status === "pending" && (
                        <Timer color="#757575" />
                      )}
                    </IconButton>
                  </div>
                  <Typography variant="body2" color="textSecondary">
                    {instance.symbols &&
                      instance.symbols.map((symbol) => symbol.name).join(" | ")}
                  </Typography>
                  <div className="d-flex justify-content-between mt-2">
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        Balance
                      </Typography>
                      <Typography>
                        ${Number(instance.balance || 0).toFixed(2)}
                      </Typography>
                    </div>
                    <div>
                      <Typography variant="body2" color="textSecondary">
                        P&L
                      </Typography>
                      <Typography
                        style={{
                          color:
                            Number(instance.profit) >= 0
                              ? "#4caf50"
                              : "#f44336",
                        }}
                      >
                        {Number(instance.profit) >= 0 ? "+" : ""}$
                        {Number(instance.profit || 0).toFixed(2)}
                      </Typography>
                    </div>
                  </div>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="mt-2"
                  >
                    Active since:{" "}
                    {new Date(instance.created_at).toLocaleDateString("en-US", {
                      month: "2-digit",
                      day: "2-digit",
                      year: "2-digit",
                    })}{" "}
                    {new Date(instance.created_at).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZone: "America/Los_Angeles",
                    })}{" "}
                    PST
                  </Typography>
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="outline-primary"
                      className="w-50"
                      onClick={() => handleInstanceClick(instance.id)}
                    >
                      Manage
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className="w-50"
                      onClick={() => {
                        navigate(`/instances/${instance.id}`);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
      <AddBrokerModal
        show={showAddBrokerModal}
        onHide={() => setShowAddBrokerModal(false)}
        selectedBroker={selectedBroker}
        setSelectedBroker={setSelectedBroker}
        handleAddBroker={handleAddBroker}
        brokersList={brokersList}
        existingBrokers={brokers}
        isLoading={isAddingBroker}
      />

      <AddAccountModal
        show={showAddAccountModal}
        onHide={() => setShowAddAccountModal(false)}
        selectedBroker={selectedBroker}
        accountId={accountId}
        setAccountId={setAccountId}
        apiKey={apiKey}
        setApiKey={setApiKey}
        apiSecret={apiSecret}
        setApiSecret={setApiSecret}
        serverName={serverName}
        setServerName={setServerName}
        accountNumber={accountNumber}
        setAccountNumber={setAccountNumber}
        accountType={accountType}
        setAccountType={setAccountType}
        handleAddAccount={handleAddAccount}
        isLoading={isAddingAccount}
      />

      <StartInstanceModal
        show={showStartInstanceModal}
        onHide={() => setShowStartInstanceModal(false)}
        newInstanceName={newInstanceName}
        setNewInstanceName={setNewInstanceName}
        selectedAccount={selectedAccount}
        setSelectedAccount={setSelectedAccount}
        selectedAlgos={selectedAlgos}
        setSelectedAlgos={setSelectedAlgos}
        brokers={brokers}
        algos={algos}
        handleStartInstance={handleStartInstance}
        telegramEnabled={telegramEnabled}
        setTelegramEnabled={setTelegramEnabled}
        telegramBotToken={telegramBotToken}
        setTelegramBotToken={setTelegramBotToken}
        telegramChatId={telegramChatId}
        setTelegramChatId={setTelegramChatId}
        telegramNotifications={telegramNotifications}
        setTelegramNotifications={setTelegramNotifications}
        isLoading={isStartingInstance}
      />
    </div>
  );
};

export default LiveTrading;
